import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Tabs,
  Tab,
  Spinner,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";

import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import DepartmentTopbar from "./SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "./SuperAdmin/components/DepertmentSidebar";

// Reusable component for summary cards
const SummaryCard = ({ title, count, totalAmount }) => (
  <Col md={4}>
    <Card className="text-center mb-3 shadow-sm">
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        <Card.Text className="h4">{count} transactions</Card.Text>
        <Card.Text className="h5 text-success">
          ₹{totalAmount.toLocaleString()}
        </Card.Text>
      </Card.Body>
    </Card>
  </Col>
);

function PaymentReports() {
  const [key, setKey] = useState("trade"); // To control active tab
  const [tradeData, setTradeData] = useState(null);
  const [advertisementData, setAdvertisementData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for filters
  const [filterType, setFilterType] = useState("today"); // 'today', 'week', 'month', 'custom'
  const [tradeTypeFilter, setTradeTypeFilter] = useState("all"); // 'all', 'register', 'renew'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchTradeReports = async () => {
    setLoading(true);
    let url = `${API_URL}trade-payment-history-filter.php`;
    const params = new URLSearchParams();

    if (filterType === "custom" && startDate && endDate) {
      params.append("start_date", startDate);
      params.append("end_date", endDate);
    } else if (filterType !== "custom") {
      params.append("filter", filterType);
    }

    if (tradeTypeFilter !== "all") {
      params.append("type", tradeTypeFilter);
    }

    url += `?${params.toString()}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setTradeData(response.data);
      } else {
        toast.error(response.data.message || "Failed to fetch trade reports.");
        setTradeData(null);
      }
    } catch (error) {
      toast.error("An error occurred while fetching trade reports.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisementReports = async () => {
    setLoading(true);
    let url = `${API_URL}advertisements-payment-history.php`;
    const params = new URLSearchParams();

    if (filterType === "custom" && startDate && endDate) {
      params.append("start_date", startDate);
      params.append("end_date", endDate);
    } else if (filterType !== "custom") {
      // Assuming the ad API also accepts 'filter' param
      params.append("filter", filterType);
    }

    url += `?${params.toString()}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setAdvertisementData(response.data.data);
      } else {
        toast.error(
          response.data.message || "Failed to fetch advertisement reports."
        );
        setAdvertisementData([]);
      }
    } catch (error) {
      toast.error("An error occurred while fetching advertisement reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key === "trade") {
      fetchTradeReports();
    } else if (key === "advertisement") {
      fetchAdvertisementReports();
    }
  }, [key, filterType, tradeTypeFilter]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (key === "trade") {
      fetchTradeReports();
    } else {
      fetchAdvertisementReports();
    }
  };

  const combinedTradeData = tradeData
    ? [...(tradeData.register?.data || []), ...(tradeData.renew?.data || [])]
    : [];

  const tradeColumns = [
    { name: "Type", selector: (row) => row.type, sortable: true },
    { name: "Trade Name", selector: (row) => row.trade_name, sortable: true },
    { name: "Owner", selector: (row) => row.trade_owner, sortable: true },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.amount).toLocaleString(),
      sortable: true,
    },
    { name: "Payment Date", selector: (row) => row.reg_date, sortable: true },
    { name: "Expire Date", selector: (row) => row.expire_date, sortable: true },
  ];

  const adColumns = [
    { name: "Agency", selector: (row) => row.agency, sortable: true },
    { name: "Location", selector: (row) => row.location, sortable: true },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.amount).toLocaleString(),
      sortable: true,
    },
    { name: "Start Date", selector: (row) => row.start_date, sortable: true },
    { name: "End Date", selector: (row) => row.end_date, sortable: true },
  ];

  const handleExport = (data, fileName, sheetName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              style={{
                backgroundColor: "#e3f2fd",
                borderBottom: "2px solid #1976d2",
              }}
            >
              <span style={{ color: "#1976d2" }}>
                Payment & Collection Reports
              </span>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleFilterSubmit}>
                <Row className="align-items-end g-3 mb-4">
                  <Col md={3}>
                    <Button
                      variant={
                        filterType === "today" ? "primary" : "outline-primary"
                      }
                      className="w-100"
                      onClick={() => setFilterType("today")}
                    >
                      Today
                    </Button>
                  </Col>
                  <Col md={3}>
                    <Button
                      variant={
                        filterType === "week" ? "primary" : "outline-primary"
                      }
                      className="w-100"
                      onClick={() => setFilterType("week")}
                    >
                      This Week
                    </Button>
                  </Col>
                  <Col md={3}>
                    <Button
                      variant={
                        filterType === "month" ? "primary" : "outline-primary"
                      }
                      className="w-100"
                      onClick={() => setFilterType("month")}
                    >
                      This Month
                    </Button>
                  </Col>
                </Row>
                <Row className="align-items-end g-3 mb-4">
                  <Col md={4}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setFilterType("custom");
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setFilterType("custom");
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Button type="submit" className="w-100">
                      Apply Custom Filter
                    </Button>
                  </Col>
                </Row>
              </Form>

              <Tabs
                activeKey={key}
                onSelect={(k) => setKey(k)}
                id="report-tabs"
                className="mb-3"
              >
                <Tab eventKey="trade" title="Trade License">
                  {loading ? (
                    <Spinner animation="border" />
                  ) : (
                    <>
                      <Row className="my-3">
                        <SummaryCard
                          title="Total Registrations"
                          count={tradeData?.register?.count || 0}
                          totalAmount={tradeData?.register?.total_amount || 0}
                        />
                        <SummaryCard
                          title="Total Renewals"
                          count={tradeData?.renew?.count || 0}
                          totalAmount={tradeData?.renew?.total_amount || 0}
                        />
                        <SummaryCard
                          title="Grand Total"
                          count={
                            (tradeData?.register?.count || 0) +
                            (tradeData?.renew?.count || 0)
                          }
                          totalAmount={
                            (tradeData?.register?.total_amount || 0) +
                            (tradeData?.renew?.total_amount || 0)
                          }
                        />
                      </Row>
                      <Row className="mb-3 align-items-center">
                        <Col md={4}>
                          <Form.Select
                            value={tradeTypeFilter}
                            onChange={(e) => setTradeTypeFilter(e.target.value)}
                          >
                            <option value="all">Filter by Type: All</option>
                            <option value="register">Register</option>
                            <option value="renew">Renew</option>
                          </Form.Select>
                        </Col>
                        <Col className="text-end">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() =>
                              handleExport(
                                combinedTradeData,
                                "TradeLicense_Report",
                                "Trades"
                              )
                            }
                          >
                            <FaFileExcel className="me-2" /> Export
                          </Button>
                        </Col>
                      </Row>
                      <DataTable
                        columns={tradeColumns}
                        data={combinedTradeData}
                        pagination
                        responsive
                      />
                    </>
                  )}
                </Tab>
                <Tab eventKey="advertisement" title="Advertisements">
                  {loading ? (
                    <Spinner animation="border" />
                  ) : (
                    <>
                      <div className="text-end my-3">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() =>
                            handleExport(
                              advertisementData,
                              "Advertisement_Report",
                              "Ads"
                            )
                          }
                        >
                          <FaFileExcel className="me-2" /> Export
                        </Button>
                      </div>
                      <DataTable
                        columns={adColumns}
                        data={advertisementData}
                        pagination
                        responsive
                      />
                    </>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default PaymentReports;
